import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useCategorizationLearning from '@/hooks/useCategorizationLearning';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeCategoriesService from '@/services/financeCategories';
import financeRulesService from '@/services/financeRules';
import financeVendorsService from '@/services/financeVendors';
import { FetchApiIds } from '@/types';
import { FxCategorizationRule, FxVendor, FxCategory } from '@/types/api/finance';
import FinanceXRulesComponent from './FinanceXRules.component';

const FinanceXRulesContainer = function (): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const orgId = getActiveOrgId();
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<FxCategorizationRule | null>(null);

  const rulesResponse = useApiFetch<{ data: FxCategorizationRule[] }, void>(
    [FetchApiIds.FX_CATEGORIZATION_RULES, orgId],
    async () => financeRulesService.getAll({ page: 1, take: 100 }),
    { retry: false }
  );

  const vendorsResponse = useApiFetch<{ data: FxVendor[] }, void>(
    [FetchApiIds.FX_VENDORS, orgId],
    async () => financeVendorsService.getAll({ page: 1, take: 200 }),
    { retry: false }
  );

  const categoriesResponse = useApiFetch<FxCategory[], void>(
    [FetchApiIds.FX_CATEGORIES, orgId],
    async () => financeCategoriesService.getAll(),
    { retry: false }
  );

  const rules = useMemo(() => (rulesResponse.data as any)?.data ?? [], [rulesResponse.data]);

  const vendors = useMemo(() => (vendorsResponse.data as any)?.data ?? [], [vendorsResponse.data]);

  const categories = useMemo(
    () => (categoriesResponse.data as FxCategory[]) ?? [],
    [categoriesResponse.data]
  );

  const { isLearning, learnResult, triggerLearning } = useCategorizationLearning({
    vendors,
    categories,
    rules,
  });

  const createMutation = useApiSubmit<FxCategorizationRule, any>(
    (payload) => financeRulesService.create(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_CATEGORIZATION_RULES);
        setShowForm(false);
        toast.showSuccessFeedback('Rule created.');
      },
    }
  );

  const updateMutation = useApiSubmit<FxCategorizationRule, any>(
    (payload) => financeRulesService.update(payload.id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_CATEGORIZATION_RULES);
        setShowForm(false);
        setEditingRule(null);
        toast.showSuccessFeedback('Rule updated.');
      },
    }
  );

  const deleteMutation = useApiSubmit<void, any>(
    (payload) => financeRulesService.delete(payload.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_CATEGORIZATION_RULES);
        toast.showSuccessFeedback('Rule deleted.');
      },
    }
  );

  const handleSubmit = useCallback(
    (data: any) => {
      if (editingRule) {
        updateMutation.mutate({ id: editingRule.id, ...data });
      } else {
        createMutation.mutate(data);
      }
    },
    [editingRule, createMutation, updateMutation]
  );

  const handleEdit = useCallback((rule: FxCategorizationRule) => {
    setEditingRule(rule);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(
    (rule: FxCategorizationRule) => {
      deleteMutation.mutate({ id: rule.id });
    },
    [deleteMutation]
  );

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingRule(null);
  }, []);

  return (
    <FinanceXRulesComponent
      rules={rules}
      vendors={vendors}
      categories={categories}
      isLoading={rulesResponse.isLoading}
      showForm={showForm}
      editingRule={editingRule}
      onNewRule={() => setShowForm(true)}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onSubmit={handleSubmit}
      onCloseForm={handleCloseForm}
      isSubmitting={createMutation.isLoading || updateMutation.isLoading}
      isLearning={isLearning}
      learnResult={learnResult}
      onLearnAndOptimize={triggerLearning}
    />
  );
};

export default FinanceXRulesContainer;
